import { useState } from 'react';
import { WhatsAppWidget } from 'react-whatsapp-widget';
import 'react-whatsapp-widget/dist/index.css';
import logoUrl from '../assets/logo.png';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import {
  GraduationCap,
  Menu,
  Search,
  User,
  LogOut,
  LayoutDashboard,
  ChevronDown,
  BookOpen,
  Users,
  Phone,
  Image,
  Award,
  Info,
  Facebook,
  Twitter,
  Instagram,
  Youtube
} from 'lucide-react';

const navLinks = [
  { name: 'Home', path: '/', icon: GraduationCap },
  { name: 'About', path: '/about', icon: Info },
  { name: 'Courses', path: '/courses', icon: BookOpen },
  { name: 'Faculty', path: '/faculty', icon: Users },
  { name: 'Scholarship', path: '/scholarship', icon: Award },
  { name: 'Gallery', path: '/gallery', icon: Image },
  { name: 'Contact', path: '/contact', icon: Phone },
];

export const Header = () => {
  const { isAuthenticated, isAdmin, user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getDashboardLink = () => {
    if (isAdmin) return '/admin';
    return '/dashboard';
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="header-logo">
            <div className="w-10 h-10 rounded-xl overflow-hidden flex items-center justify-center">
              <img src={logoUrl} alt="Caliber Nova" className="w-full h-full object-contain" />
            </div>
            <div className="hidden sm:block">
              <span className="font-heading font-bold text-xl text-primary">Caliber Nova</span>
              <span className="block text-xs text-muted-foreground -mt-1">Excellence in JEE & NEET Coaching</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.name.toLowerCase()}`}
                className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${location.pathname === link.path
                  ? 'text-primary bg-primary/5'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {/* Search */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSearchOpen(!searchOpen)}
              data-testid="search-toggle"
              className="hidden sm:flex"
            >
              <Search className="w-5 h-5" />
            </Button>

            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="gap-2" data-testid="user-menu-trigger">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="w-4 h-4 text-primary" />
                    </div>
                    <span className="hidden sm:block text-sm font-medium">{user?.name?.split(' ')[0]}</span>
                    <ChevronDown className="w-4 h-4 hidden sm:block" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem asChild>
                    <Link to={getDashboardLink()} className="cursor-pointer" data-testid="dashboard-link">
                      <LayoutDashboard className="w-4 h-4 mr-2" />
                      Dashboard
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-destructive" data-testid="logout-btn">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2">
                <Button variant="ghost" asChild className="hidden sm:flex" data-testid="login-btn">
                  <Link to="/login">Login</Link>
                </Button>
                <Button asChild data-testid="register-btn" className="bg-primary hover:bg-primary/90">
                  <Link to="/register">Get Started</Link>
                </Button>
              </div>
            )}

            {/* Mobile Menu */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-trigger">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72">
                <div className="flex flex-col gap-4 mt-8">
                  {navLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={() => setMobileOpen(false)}
                      className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${location.pathname === link.path
                        ? 'text-primary bg-primary/5'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                        }`}
                    >
                      <link.icon className="w-5 h-5" />
                      {link.name}
                    </Link>
                  ))}
                  {!isAuthenticated && (
                    <div className="pt-4 border-t">
                      <Button asChild className="w-full">
                        <Link to="/login" onClick={() => setMobileOpen(false)}>Login</Link>
                      </Button>
                    </div>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>

        {/* Search Bar */}
        {searchOpen && (
          <div className="py-3 border-t animate-fade-in">
            <div className="relative max-w-xl mx-auto">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search courses, faculty, notices..."
                className="pl-10"
                data-testid="search-input"
              />
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export const Footer = () => {
  return (
    <footer className="bg-slate-900 text-slate-300">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 rounded-xl overflow-hidden flex items-center justify-center bg-white p-1">
                <img src={logoUrl} alt="Caliber Nova" className="w-full h-full object-contain" />
              </div>
              <div>
                <span className="font-heading font-bold text-xl text-white">Caliber Nova</span>
                <span className="block text-xs text-slate-400 -mt-1">Excellence in JEE & NEET Coaching</span>
              </div>
            </div>
            <p className="text-sm text-slate-400 mb-4">
              Your gateway to IITs & Top Medical Colleges. Expert coaching for JEE & NEET preparation.
            </p>
            <div className="flex gap-3">
              {[
                { name: 'facebook', href: 'https://facebook.com', Icon: Facebook },
                { name: 'twitter', href: 'https://twitter.com', Icon: Twitter },
                { name: 'instagram', href: 'https://instagram.com', Icon: Instagram },
                { name: 'youtube', href: 'https://youtube.com', Icon: Youtube },
              ].map(({ name, href, Icon }) => (
                <a
                  key={name}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-lg bg-slate-800 hover:bg-primary flex items-center justify-center transition-colors"
                  data-testid={`social-${name}`}
                >
                  <span className="sr-only">{name}</span>
                  <Icon className="w-4 h-4 text-slate-300" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading font-semibold text-white mb-4">Quick Links</h4>
            <ul className="space-y-2">
              {[
                { name: 'Home', path: '/' },
                { name: 'About Us', path: '/about' },
                { name: 'Courses', path: '/courses' },
                { name: 'Gallery', path: '/gallery' },
                { name: 'Contact Us', path: '/contact' },
                { name: 'Terms & Conditions', path: '/terms' },
                { name: 'Refund Policy', path: '/refund' },
                { name: 'Privacy Policy', path: '/privacy' }
              ].map((link) => (
                <li key={link.name}>
                  <Link to={link.path} className="text-sm hover:text-white transition-colors">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Programs */}
          <div>
            <h4 className="font-heading font-semibold text-white mb-4">Programs</h4>
            <ul className="space-y-2">
              {['JEE Mains', 'JEE Advanced', 'NEET UG', 'NEET PG', 'Foundation'].map((program) => (
                <li key={program}>
                  <Link to="/courses" className="text-sm hover:text-white transition-colors">
                    {program}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-heading font-semibold text-white mb-4">Contact Us</h4>
            <ul className="space-y-3 text-sm">
              <li>
                <span className="text-slate-400">Address:</span><br />
                2nd Floor, Zenda Chowk, Mahal, Nagpur - 440032
              </li>
              <li>
                <span className="text-slate-400">Phone:</span><br />
                <a href="tel:+919595253778" className="hover:text-white transition-colors">+91 9595253778</a>
                {' | '}
                <a href="tel:+919527225500" className="hover:text-white transition-colors">+91 9527225500</a>
              </li>
              <li>
                <span className="text-slate-400">Email:</span><br />
                <a href="mailto:info@calibernova.in" className="hover:text-white transition-colors">info@calibernova.in</a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-slate-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-slate-500">
            © {new Date().getFullYear()} Caliber Nova (Caliber Nova). All rights reserved.
          </p>
          <p className="text-sm text-slate-500">
            Disclaimer: *Terms and conditions apply to the 100% Money Back Guarantee.
          </p>
          <div className="flex gap-6 text-sm">
            <Link to="/terms" className="hover:text-white transition-colors">Terms & Conditions</Link>
            <Link to="/refund" className="hover:text-white transition-colors">Refund Policy</Link>
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export const Layout = ({ children }) => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
      <WhatsAppWidget
        phoneNumber="919595253778"
        companyName="Caliber Nova"
        message="Hello! How can we help you with your JEE/NEET preparation?"
        replyTimeText="Typically replies within minutes"
        CompanyIcon={() => <img src={logoUrl} alt="Caliber Nova" style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '50%', background: 'white', padding: '0px' }} />}
      />
    </div>
  );
};

export default Layout;
